const DEVICE_ID_KEY = 'sabibooks-device-id';

/**
 * Get or create a persistent device ID.
 * Stored in localStorage and persists across sessions.
 */
export function getDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Read-only check: returns the stored device ID without creating one if absent.
 *
 * Used by the boot-sync effect in App.tsx to gate the `/auth/devices/me` call —
 * if localStorage has no value, we genuinely need the cookie resolution; if it has
 * a value AND the user has logged in before, we can skip the network round trip.
 */
export function peekDeviceId(): string | null {
  return localStorage.getItem(DEVICE_ID_KEY);
}

/**
 * Overwrite the local device ID with the one resolved from the `sb_device` cookie.
 *
 * Called on app boot when GET /auth/devices/me returns a row — keeps localStorage in
 * sync with the cookie-side source of truth so the UI's `isCurrentDeviceTrusted`
 * lookups continue to match after an iOS Safari ITP wipe. No-op when the value is
 * already correct.
 */
export function setDeviceId(deviceId: string): void {
  if (localStorage.getItem(DEVICE_ID_KEY) !== deviceId) {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
}

interface DeviceInfo {
  deviceName: string;
  deviceType: 'MOBILE' | 'TABLET' | 'DESKTOP';
}

/**
 * Extract device name and type from the user agent string.
 */
export function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;

  const deviceType = getDeviceType(ua);
  const deviceName = getDeviceName(ua, deviceType);

  return { deviceName, deviceType };
}

function getDeviceType(ua: string): DeviceInfo['deviceType'] {
  if (/iPad|Android(?!.*Mobile)/i.test(ua)) return 'TABLET';
  if (/Mobile|iPhone|iPod|Android.*Mobile|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua)) return 'MOBILE';
  return 'DESKTOP';
}

function getDeviceName(ua: string, deviceType: DeviceInfo['deviceType']): string {
  // Try to extract OS/browser info for a human-readable name
  let os = 'Unknown';
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Macintosh|Mac OS/i.test(ua)) os = 'Mac';
  else if (/iPhone/i.test(ua)) os = 'iPhone';
  else if (/iPad/i.test(ua)) os = 'iPad';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/Linux/i.test(ua)) os = 'Linux';
  else if (/CrOS/i.test(ua)) os = 'ChromeOS';

  let browser = 'Browser';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome/i.test(ua)) browser = 'Chrome';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Safari/i.test(ua)) browser = 'Safari';
  else if (/Opera|OPR/i.test(ua)) browser = 'Opera';

  return `${os} ${browser} (${deviceType.charAt(0) + deviceType.slice(1).toLowerCase()})`;
}
